import { CaretDown, CaretUp, Check, Globe, Moon, Sun } from '@phosphor-icons/react';
import { LANGS, type LangCode } from '@sm/i18n';
import { DropdownMenu as M } from 'radix-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cx } from '@/components/ui/cx';
import { useLanguage } from '@/hooks/usePreferences';

/** Izbor jezika iz dizajna: SR / EN / DE („Uskoro“, onemogućeno). */
export function LanguageMenu({ persistRemote = false }: { persistRemote?: boolean }) {
  const { t } = useTranslation();
  const { lang, change } = useLanguage(persistRemote);
  const [open, setOpen] = useState(false);
  const cur = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  return (
    <M.Root open={open} onOpenChange={setOpen}>
      <M.Trigger
        className={cx(
          'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-[13px] leading-none font-semibold tracking-[.02em] whitespace-nowrap transition-colors',
          'hover:border-accent hover:bg-chip hover:text-accent',
          open ? 'border-accent bg-chip text-accent' : 'border-line2 bg-transparent text-text',
        )}
      >
        <Globe size={17} aria-hidden />
        {/* Ime dugmeta = skriveni opis + vidljivi kod („Jezik: Srpski SR“), pa vidljivi tekst ostaje dio imena
            (WCAG 2.5.3 Label in Name). */}
        <span className="sr-only">
          {t('panel:shell.language.trigger', { language: cur.native })}
        </span>
        {cur.code.toUpperCase()}
        {open ? <CaretUp size={13} /> : <CaretDown size={13} />}
      </M.Trigger>
      <M.Portal>
        <M.Content
          align="end"
          sideOffset={8}
          className="z-[61] min-w-[250px] rounded-xl border border-line bg-surface p-1.5 shadow-card"
        >
          <M.Label className="block px-2.5 pt-2 pb-1.5 text-xs font-semibold tracking-[.08em] text-muted uppercase">
            {t('panel:shell.language.label')}
          </M.Label>
          <M.RadioGroup
            value={lang}
            onValueChange={(v) => change(v as LangCode)}
            className="flex flex-col gap-0.5"
          >
            {LANGS.map((l) => {
              const on = l.code === lang;
              return (
                <M.RadioItem
                  key={l.code}
                  value={l.code}
                  disabled={!l.ready}
                  className={cx(
                    'flex min-h-11 items-center gap-3 rounded-md px-2.5 outline-none select-none data-[highlighted]:bg-bg2',
                    l.ready ? 'cursor-pointer' : 'cursor-not-allowed opacity-55',
                    on ? 'text-accent' : 'text-text',
                  )}
                >
                  <span className="grid h-6 min-w-[30px] flex-none place-items-center rounded-[5px] border border-line bg-bg2 px-1.5 text-[11px] font-semibold tracking-[.04em] text-text2">
                    {l.code.toUpperCase()}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-px">
                    <span lang={l.lang} className="text-sm font-semibold">
                      {l.native}
                    </span>
                    <span className="text-xs text-muted">
                      {t(`common:language.description.${l.code}`)}
                    </span>
                  </span>
                  {on ? <Check size={17} className="text-accent" /> : null}
                  {!l.ready ? (
                    <span className="rounded-sm bg-chip px-[7px] py-0.5 text-[10px] font-semibold tracking-[.06em] text-accent uppercase">
                      {t('panel:shell.language.comingSoon')}
                    </span>
                  ) : null}
                </M.RadioItem>
              );
            })}
          </M.RadioGroup>
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

export function ThemeToggleButton({
  theme,
  onToggle,
}: {
  theme: 'light' | 'dark';
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const label =
    theme === 'dark' ? t('panel:shell.theme.switchToLight') : t('panel:shell.theme.switchToDark');
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={label}
      className="grid h-10 w-10 place-items-center rounded-md border border-line2 bg-transparent text-text transition-colors hover:border-accent hover:text-accent"
    >
      <ThemeIcon theme={theme} />
    </button>
  );
}

function ThemeIcon({ theme }: { theme: 'light' | 'dark' }) {
  return theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />;
}
